import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import type { DocumentTemplateDetail } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HeaderActions } from '@/components/layout/AppShell';
import type { AppShellContext } from '@/components/layout/AppShell';
import { TemplateEditor } from '@/components/documents/TemplateEditor';
import { useDocumentsApi } from '@/services/api';
import { t } from '@/i18n';

/**
 * Template editor page (Task 6, spec §8.3): loads the template draft and
 * hands the split-pane editor its data; the actual editor UI lives in
 * TemplateEditor.tsx so this page only handles loading/not-found and the
 * back-to-list header.
 */
export function TemplateEditorPage() {
  const { nip } = useOutletContext<AppShellContext>();
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { getTemplate } = useDocumentsApi();

  const [template, setTemplate] = useState<DocumentTemplateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const loadTemplate = useCallback(async () => {
    if (!templateId) return;
    try {
      setLoading(true);
      setNotFound(false);
      setTemplate(await getTemplate(nip, templateId));
    } catch (error) {
      setNotFound(true);
      toast.error(t('documents.templates.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getTemplate, nip, templateId]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const backButton = (
    <Button variant="ghost" size="sm" onClick={() => navigate(`/${nip}/documents`)}>
      <ArrowLeft className="size-4" />
      {t('documents.editor.back')}
    </Button>
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-muted-foreground size-8 animate-spin" />
      </div>
    );
  }

  if (notFound || !template) {
    return (
      <div className="flex flex-col items-center gap-4 p-10 text-center">
        <p className="text-muted-foreground">{t('errors.notFound')}</p>
        {backButton}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <HeaderActions>{backButton}</HeaderActions>

      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold tracking-tight">{template.name}</h1>
        <Badge variant="outline">{t(`documents.templates.categories.${template.category}`)}</Badge>
        <Badge variant="outline">
          {template.currentVersion > 0
            ? `v${template.currentVersion}`
            : t('documents.templates.draft')}
        </Badge>
      </div>

      {/* Keyed on id so switching templates (a fresh getTemplate load) always
          re-initializes the editor's draft/autosave state instead of reusing
          a mounted instance whose internal state was seeded from the previous
          template. */}
      <TemplateEditor key={template.id} nip={nip} template={template} onTemplateChange={setTemplate} />
    </div>
  );
}
